import React, {FC, useEffect, useState} from "react";
import {
    EuiButton,
    EuiDatePicker,
    EuiFieldText,
    EuiFlexGroup,
    EuiFlexItem,
    EuiFormRow,
    EuiPanel,
    EuiSpacer,
} from "@elastic/eui";
import {INewOrderDetails} from "../../pages/newOrder/useNewOrderDetails";
import {Business} from "api/entities/Business";
import {StripeAddPaymentMethod} from "../payments/StripeAddPaymentMethod";
import {
    useAddPlanToBusinessMutation,
    useGetDefaultPaymentMethodQuery,
    useSyncCardsMutation,
} from "../../api/rtkQueryApi/BusinessesApi";
import {PurchaseLegalSignaturePdf} from "./PurchaseLegalSignaturePdf";
import {ReactSketchCanvas} from "react-sketch-canvas";
import {PaymentMethodCard} from "../../api/entities/PaymentMethodCard";
import {
    useActivatePlanMutation,
    useCreateAgreementMutation,
    useEmailPdfMutation,
} from "../../api/rtkQueryApi/PlansApi";
import {Document, Page, usePDF} from "@react-pdf/renderer";
import {useSignAgreementMutation} from "../../api/rtkQueryApi/AgreementsApi";
import {useDebounceEffect} from "../../utils/useDebounceEffect";
import moment from "moment";

export interface IAddPlanFinalize {
    business: Business;
    orderDetails: INewOrderDetails;
    onPurchased: () => void;
    onBackClick: () => void;
}

export const AddPlanFinalize: FC<IAddPlanFinalize> = (props) => {
    const [defaultPaymentMethod, setDefaultPaymentMethod] =
        useState<PaymentMethodCard>();

    const [planName, setPlanName] = useState(
        props.orderDetails.productGroup?.name ?? "",
    );
    const [startDate, setStartDate] = useState<string>(moment().format("MMMM DD, YYYY"));

    const [hasDrawn, setHasDrawn] = useState(false);
    const signatureRef = React.useRef<any>();
    const [signatureUrl, setSignatureUrl] = React.useState<string>();
    const [pdfKey, setPdfKey] = React.useState(0);

    const [addPlanToBusiness, addPlanToBusinessRequest] =
        useAddPlanToBusinessMutation();
    const [createAgreement, createAgreementRequest] =
        useCreateAgreementMutation();
    const [syncCards, syncCardsRequest] = useSyncCardsMutation();
    const [emailPdf, emailPdfRequest] = useEmailPdfMutation();
    const [activatePlan, activatePlanRequest] = useActivatePlanMutation();
    const [signAgreement] = useSignAgreementMutation();
    const getDefaultPaymentMethodQuery = useGetDefaultPaymentMethodQuery({
        businessId: props.business.id ?? "",
    });
    const [legalText, setLegalText] = useState("");
    const [confirmClicked, setConfirmClicked] = useState(false);
    const [finalizePlanReady, setFinalizePlanReady] = useState<{
        finalize: boolean;
        signAndActivate: boolean;
    }>({finalize: false, signAndActivate: false});
    const [instance, updateInstance] = usePDF({
        document: (
            <Document>
                <Page size="A4"/>
            </Document>
        ),
    });

    useEffect(() => {
        if (getDefaultPaymentMethodQuery.data) {
            setDefaultPaymentMethod(getDefaultPaymentMethodQuery.data);
        }
    }, [getDefaultPaymentMethodQuery.data]);

    const onDocumentChange = (document: React.ReactElement) => {
        if (instance.loading) return;

        updateInstance(document);
    };

    const onCardAdded = () => {
        setFinalizePlanReady({finalize: true, signAndActivate: true});
    };

    const onRemoteSignClick = () => {
        setFinalizePlanReady({finalize: true, signAndActivate: false});
    };

    useDebounceEffect(
        1000,
        () => {
            if (
                !finalizePlanReady.finalize ||
                !props.business.id ||
                instance.loading ||
                instance.error ||
                !instance.blob ||
                !props.orderDetails.productId ||
                !startDate
            ) {
                console.log(startDate);
                return;
            }
            const businessId = props.business.id;
            const productId = props.orderDetails.productId;
            const blob = instance.blob;

            const runFunc = async () => {
                const addPlanResponse = await addPlanToBusiness({
                    planName: planName,
                    businessId: businessId,
                    productId: productId,
                    addAddonRequests: props.orderDetails.addons?.map((addon) => ({
                        addonId: addon.referenceId,
                        contractLength: addon.contractLength,
                        startDate: addon.startDate
                            ? moment(addon.startDate).set("hour", 12).utc()
                            : undefined,
                    })),
                    discount: props.orderDetails.discount ?? 0,
                    isOverrideSetupFee: props.orderDetails.isOverrideSetupFee,
                    overrideSetupFee: props.orderDetails.overrideSetupFee,
                    startDate: moment(startDate).set("hour", 12).utc(),
                });

                const planId = addPlanResponse.data?.planId;
                if (planId) {
                    const agreementResponse = await createAgreement({
                        planId: planId,
                        content: legalText,
                        pdfUrl: signatureUrl ?? "",
                    });

                    const agreementId = agreementResponse.data?.id;

                    if (agreementId && finalizePlanReady.signAndActivate) {
                        const file = new File([blob], `purchase_summary.pdf`, {
                            type: "application/pdf",
                        });

                        await emailPdf({
                            businessId: businessId,
                            file: file,
                        });

                        await signAgreement({agreementId: agreementId});
                        await syncCards({businessId: businessId});
                        await activatePlan({planId: planId});
                    }

                    props.onPurchased();
                }
            };
            runFunc();
        },
        [
            finalizePlanReady,
            instance.loading,
            instance.blob,
            instance.url,
            props.business.id,
            props.orderDetails.productId,
        ],
    );

    const onBackClick = () => {
        props.onBackClick();
    };

    const onSignCompleteClick = () => {
        setConfirmClicked(true);
        const exportPromise = signatureRef.current?.exportImage();
        return exportPromise
            ?.then((signature: string) => {
                if (signature) {
                    setSignatureUrl(signature);
                    setPdfKey(pdfKey + 1);
                }
            })
            .finally(() => {
                signatureRef.current?.clearCanvas();
                setHasDrawn(false);
            });
    };

    const onConfirmWithDefaultPaymentMethod = () => {
        onSignCompleteClick().then(() => {
            onCardAdded();
        });
    };

    const isLoading =
        syncCardsRequest.isLoading ||
        addPlanToBusinessRequest.isLoading ||
        emailPdfRequest.isLoading ||
        activatePlanRequest.isLoading ||
        createAgreementRequest.isLoading;

    return (
        <EuiPanel
            hasBorder={false}
            hasShadow={false}
            style={{overflow: "auto", padding: 12, minWidth: 600}}
        >
            <PurchaseLegalSignaturePdf
                orderDetails={props.orderDetails}
                business={props.business}
                pdfKey={pdfKey}
                signatureUrl={signatureUrl}
                onDocumentChange={onDocumentChange}
                onLegalTextChange={setLegalText}
            />
            {!confirmClicked && (
                <EuiFormRow label={"Sign Here"} fullWidth>
                    <ReactSketchCanvas
                        width="100%"
                        height="180px"
                        canvasColor="transparent"
                        strokeColor="#000000"
                        ref={signatureRef}
                        onStroke={() => setHasDrawn(true)}
                    />
                </EuiFormRow>
            )}
            <EuiFormRow label={"Plan Name"}>
                <EuiFieldText
                    placeholder={"Plan Name"}
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                />
            </EuiFormRow>
            <EuiFormRow label={"Select Start Date"}>
                <EuiDatePicker value={startDate} onChange={d => setStartDate(moment(d).format("MMMM DD, YYYY"))} minDate={moment()} />
            </EuiFormRow>
            {!defaultPaymentMethod?.paymentMethodId &&
            !getDefaultPaymentMethodQuery.isLoading ? (
                <EuiFormRow>
                    <StripeAddPaymentMethod
                        return_url={window.location.href}
                        businessId={props.business.id ?? ""}
                        onPaymentAdded={onCardAdded}
                        onBackClick={onBackClick}
                        prePaymentAddedAsync={onSignCompleteClick}
                        isLoading={isLoading}
                        onRemoteSignClick={onRemoteSignClick}
                        isDisabled={!hasDrawn}
                        confirmText={"Sign & Complete"}
                        remoteSignText={"Save for Remote E-Signing"}
                    />
                </EuiFormRow>
            ) : (
                <>
                    <EuiSpacer/>
                    <EuiFlexGroup>
                        <EuiFlexItem grow={false}>
                            <EuiButton onClick={onBackClick} fill isDisabled={isLoading}>
                                Back
                            </EuiButton>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiButton
                                fill
                                isDisabled={!hasDrawn || !planName}
                                isLoading={isLoading}
                                onClick={onConfirmWithDefaultPaymentMethod}
                            >
                                Sign & Complete
                            </EuiButton>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiButton
                                fill
                                isDisabled={isLoading}
                                onClick={onRemoteSignClick}
                            >
                                Save for Remote E-Signing
                            </EuiButton>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </>
            )}
        </EuiPanel>
    );
};
